// Reopen modal (#1059): textarea + confirm. Submits the `reopen` transition,
// which moves PROCESSED -> IN_WORK, clears the winner pick, and restarts the
// SLA window so the logist can take another logistics pass. Reason is
// required (backend `ReasonRequiredError`) and recorded on the audit row.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Modal, Textarea } from '../../../../ui/index.js';
import { useRequestsStore } from '../../store.js';
import { useTransition } from '../../queries.js';
import { errorMessage } from '../../../../api/errors.js';
import type { RequestView, UserRef } from '../../types.js';

export interface ReopenRequestModalProps {
  request: RequestView;
  me: UserRef;
}

export function ReopenRequestModal({ request }: ReopenRequestModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [reason, setReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const setModal = useRequestsStore((s) => s.setModal);
  const transitionMutation = useTransition(request.id);

  const onClose = () => setModal(null);

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    try {
      await transitionMutation.mutateAsync({ key: 'reopen', body: { reason: reason.trim() } });
      setModal(null);
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('requests:reopenModal.title', { id: request.id })}
      width={520}
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="primary"
            icon="rotate-ccw"
            disabled={!reason.trim() || transitionMutation.isPending}
            onClick={() => {
              void onSubmit();
            }}
          >
            {t('requests:reopenModal.submit')}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--fg-2)', marginBottom: 12 }}>
        {t('requests:reopenModal.explanation')}
      </div>
      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginBottom: 5 }}>
          {t('requests:reopenModal.reasonLabel')}{' '}
          <span style={{ color: 'var(--status-error-fg)' }}>*</span>
        </div>
        <Textarea
          value={reason}
          onChange={(v) => setReason(v)}
          rows={4}
          placeholder={t('requests:reopenModal.reasonPlaceholder')}
        />
      </div>
      {submitError && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Icon name="alert-triangle" size={14} />
          <span>{submitError}</span>
        </div>
      )}
    </Modal>
  );
}
